import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../api";

const BETTER_BALL_HCP_FORMATS = ["Better Ball Stableford", "Better Ball Medal", "Better Ball Stroke Play", "Fourball Better Ball Stableford", "Fourball Stroke Play"];

export default function BestBallResults() {
  const [event, setEvent] = useState(null);
  const [results, setResults] = useState([]);
  const [archiveRows, setArchiveRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);
  const [toast, setToast] = useState("");
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 4000); };
  const prizesRef = React.useRef({ nearest_pin: "", nearest_pin2: "", longest_drive: "" });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) { setLoading(false); return; }
      setEvent(eventRes.data);

      const [scoresRes, groupsRes, holesRes, prizesRes] = await Promise.all([
        api.get("/scores/" + eventRes.data.id),
        api.get("/groups"),
        api.get("/courses/" + eventRes.data.course_id + "/holes"),
        api.get("/events/prizes").catch(() => ({ data: { nearest_pin: "", nearest_pin2: "", longest_drive: "" } }))
      ]);

      const players = (groupsRes.data.players || []).filter(p => p.playing);
      const holes = holesRes.data;
      const scores = scoresRes.data;
      const ghost = groupsRes.data.ghost || null;
      const isMedal = eventRes.data.game_type?.includes("Medal") || eventRes.data.game_type?.includes("Stroke");
      const isBetterBall = BETTER_BALL_HCP_FORMATS.includes(eventRes.data.game_type);

      const grpMap = {};
      const grpSizes = {};
      players.forEach(p => {
        if (!grpMap[p.grp]) grpMap[p.grp] = {};
        grpSizes[p.grp] = (grpSizes[p.grp] || 0) + 1;
        const tid = p.team_id || 0;
        if (!grpMap[p.grp][tid]) grpMap[p.grp][tid] = [];
        grpMap[p.grp][tid].push(p);
      });

      // Mirror the event's ghost (if any) into whichever team is short a
      // partner in a 3-ball group, so that group's best-ball total is
      // computed as a 4-ball would be — using the ghost's own real scores,
      // not a fabricated one.
      if (ghost) {
        Object.entries(grpMap).forEach(([grp, teamGroups]) => {
          if (grpSizes[grp] !== 3) return;
          Object.values(teamGroups).forEach(members => {
            if (members.length === 1) members.push({ player_id: ghost.id, name: "Ghost (" + ghost.name + ")", handicap: ghost.handicap, isGhost: true });
          });
        });
      }

      const pairResults = [];
      // Per real player (never the ghost), the pair's shared archived result
      // — same convention as IndividualResults.jsx's Better Ball branch:
      // whichever teammate's score wins the hole is credited to BOTH
      // partners for net/points, while each keeps their own gross.
      const playerArchive = {};

      Object.entries(grpMap).sort((a,b) => parseInt(a[0])-parseInt(b[0])).forEach(([grp, teamGroups]) => {
        Object.entries(teamGroups).forEach(([tid, members]) => {
          const names = members.map(p => p.name.split(" ")[0]).join(" / ");
          let totalBest = 0, thru = 0;

          members.forEach(p => {
            if (p.isGhost) return;
            if (!playerArchive[p.player_id]) {
              playerArchive[p.player_id] = { player_id: p.player_id, name: p.name, handicap: p.handicap, grp: parseInt(grp), gross: 0, net: 0, points: 0, holeGross: [], holePoints: [] };
            }
          });

          holes.forEach(hole => {
            // A Better Ball card always saves a row for BOTH partners on
            // every hole — whoever wasn't the counting score that hole
            // gets a 0, mirroring the blank left on the real paper card
            // (see routes/scores.js /save and BetterBallScoreEntry.jsx).
            // That 0 must be treated as "didn't play this ball," not as a
            // real score of zero, or it reads as an impossibly good hole
            // and wins the best-of-pair comparison below almost every time.
            const memberCalcs = members.map(p => {
              const s = scores.find(s => s.player_id === p.player_id && s.hole === hole.hole);
              if (!s || !parseInt(s.gross)) return null;
              // Better Ball plays off 85% of Course Handicap (hcp_adj).
              const rawHcp = (p.hcp_adj ?? p.handicap) || 0;
              const hcp = isBetterBall ? Math.round(rawHcp * 0.85) : rawHcp;
              const base = Math.floor(hcp / 18);
              const extra = hcp % 18;
              const shots = base + (hole.stroke_index <= extra ? 1 : 0);
              const g = parseInt(s.gross);
              const net = g - shots;
              const pts = Math.max(0, 2 + (hole.par - net));
              return { player_id: p.player_id, isGhost: p.isGhost, g, net, pts };
            }).filter(c => c !== null);

            if (memberCalcs.length > 0) {
              const chosen = isMedal
                ? memberCalcs.reduce((best, c) => c.net < best.net ? c : best)
                : memberCalcs.reduce((best, c) => c.pts > best.pts ? c : best);
              totalBest += isMedal ? chosen.net : chosen.pts;
              thru++;
              members.forEach(p => {
                if (p.isGhost) return;
                const rec = playerArchive[p.player_id];
                const own = memberCalcs.find(c => c.player_id === p.player_id);
                rec.gross += own ? own.g : 0;
                rec.net += chosen.net;
                rec.points += chosen.pts;
                rec.holeGross.push(own ? own.g : 0);
                rec.holePoints.push(chosen.pts);
              });
            } else {
              members.forEach(p => {
                if (p.isGhost) return;
                const rec = playerArchive[p.player_id];
                rec.holeGross.push(0);
                rec.holePoints.push(0);
              });
            }
          });

          pairResults.push({ grp: parseInt(grp), names, total: thru > 0 ? totalBest : "-", thru });
        });
      });

      pairResults.sort((a,b) => {
        if (isMedal) return (a.total==="-"?9999:a.total) - (b.total==="-"?9999:b.total);
        return (b.total==="-"?-1:b.total) - (a.total==="-"?-1:a.total);
      });
      pairResults.forEach((p,i) => { p.pos = i+1; });
      setResults(pairResults);

      // Rank per player mirrors their pair's position in the table above.
      const posByGrp = {};
      pairResults.forEach(p => { posByGrp[p.grp] = p.pos; });
      const archiveList = Object.values(playerArchive).map(r => ({
        ...r,
        pos: posByGrp[r.grp] || 0,
        gross: r.gross || 0,
        net: r.net || 0,
        points: r.points || 0,
      }));
      setArchiveRows(archiveList);

      prizesRef.current = {
        nearest_pin: prizesRes.data.nearest_pin || "",
        nearest_pin2: prizesRes.data.nearest_pin2 || "",
        longest_drive: prizesRes.data.longest_drive || "",
      };
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const archiveResults = async () => {
    if (!event || archiveRows.length === 0) return;
    if (!window.confirm("Archive these Better Ball results? This locks in the round permanently.")) return;
    setArchiving(true);
    try {
      const prizes = prizesRef.current;
      const archiveData = archiveRows.map(p => ({
        position: p.pos, rank: p.pos, player_name: p.name, handicap: p.handicap,
        gross: p.gross, net: p.net, points: p.points,
        holes: (p.holeGross || []).map((g, i) => ({ hole: i + 1, gross: g })).filter(hh => hh.gross > 0),
      }));
      const twos = [];
      archiveRows.forEach(p => { (p.holeGross || []).forEach((g, i) => { if (g === 2) twos.push({ player_name: p.name, hole: i + 1 }); }); });
      const res = await api.post("/archive/save", { event_id: event.id, results: archiveData, ...prizes, twos });
      showToast("Results archived!");
      const changes = res.data?.handicapChanges;
      if (changes && changes.length) {
        const lines = changes.map(c => `${c.name}: ${c.from} → ${c.to}`).join("\n");
        window.alert("Handicaps adjusted:\n\n" + lines);
      }
    } catch(e) { showToast("Failed: " + (e.response?.data?.error || e.message)); }
    finally { setArchiving(false); }
  };

  const isMedal = event?.game_type?.includes("Medal") || event?.game_type?.includes("Stroke");

  return (
    <Layout>
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">Better Ball Results</span>
          {event && <span className="text-xs text-blue-600 font-medium">{event.game_type}</span>}
          <button onClick={archiveResults} disabled={archiving || archiveRows.length === 0}
            className="ml-auto bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white text-xs font-semibold px-4 py-1.5 rounded">
            {archiving ? "Archiving..." : "Archive Results"}
          </button>
        </div>
        {toast && <div className="bg-blue-50 text-blue-700 px-4 py-2 text-sm border-b border-blue-200">{toast}</div>}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-green-800 text-white">
              <tr>
                <th className="px-4 py-2 text-center w-12">Pos</th>
                <th className="px-4 py-2 text-left">Pair</th>
                <th className="px-4 py-2 text-center w-16">Thru</th>
                <th className="px-4 py-2 text-center w-20">{isMedal ? "Best Net" : "Best Pts"}</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r,i) => (
                <tr key={i} className={"border-b border-gray-200 " + (i%2===0?"bg-white":"bg-green-50")}>
                  <td className="px-4 py-2 text-center font-bold">{r.pos}</td>
                  <td className="px-4 py-2">{r.names}</td>
                  <td className="px-4 py-2 text-center">{r.thru}</td>
                  <td className="px-4 py-2 text-center font-bold text-blue-700">{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && results.length === 0 && <div className="text-center py-8 text-gray-500">No scores entered yet.</div>}
        </div>
      </div>
    </Layout>
  );
}
